import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Switch,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Section, ActionBar, SettingRow } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useBucketLogging,
  useSetBucketLogging,
  s3ExtractError,
} from '@/api/s3';

/**
 * Server access-logging: when on, S3 writes access records into a target bucket
 * under an optional prefix. Targets are only meaningful (and only sent) while
 * enabled — toggling off clears them server-side.
 */
export function BucketLoggingTab({
  serviceId,
  bucket,
}: {
  serviceId: string;
  bucket: string;
}) {
  const toast = useToast();
  const { data, isLoading } = useBucketLogging(serviceId, bucket);
  const save = useSetBucketLogging(serviceId, bucket);

  const [enabled, setEnabled] = useState(false);
  const [target, setTarget] = useState('');
  const [prefix, setPrefix] = useState('');

  // Re-seed from the loaded config (and after a refetch); default target to the
  // bucket itself so a freshly-enabled log has a sensible destination.
  useEffect(() => {
    if (!data) return;
    setEnabled(data.enabled);
    setTarget(data.target_bucket ?? (data.enabled ? '' : bucket));
    setPrefix(data.target_prefix ?? '');
  }, [data, bucket]);

  const targetMissing = enabled && !target.trim();

  const onSave = () => {
    save.mutate(
      enabled
        ? { enabled, target_bucket: target.trim(), target_prefix: prefix.trim() }
        : { enabled: false },
      {
        onSuccess: () => toast.success('Access logging updated'),
        onError: (e) => toast.error(s3ExtractError(e, 'Failed to update logging')),
      },
    );
  };

  if (isLoading) return <Skeleton variant="rect" height={160} />;

  return (
    <Section
      title="Access logging"
      description="Record every request to this bucket into a target bucket."
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader>
            <CardTitle>Server access logging</CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            <SettingRow
              title="Enable access logging"
              description="Record every request to this bucket into a target bucket."
              control={
                <Switch checked={enabled} onCheckedChange={setEnabled} disabled={save.isPending} />
              }
            />

            {enabled && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 560 }}>
                <Field
                  label="Target bucket"
                  required
                  error={targetMissing ? 'Target bucket is required.' : undefined}
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={target}
                      invalid={targetMissing}
                      placeholder="e.g. my-access-logs"
                      onChange={(e) => setTarget(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Target prefix">
                  {(p) => (
                    <Input
                      {...p}
                      value={prefix}
                      placeholder="e.g. logs/"
                      onChange={(e) => setPrefix(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          end={
            <Button
              variant="primary"
              size="sm"
              disabled={save.isPending || targetMissing}
              onClick={onSave}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          }
        />
      </div>
    </Section>
  );
}
